import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

const cities = [
  { name: 'Lumberton', county: 'Robeson County', desc: 'Serving Lumberton families with mobile music lessons and creative development programs delivered right to your door.' },
  { name: 'Red Springs', county: 'Robeson County', desc: 'Bringing professional music instruction and creative coaching to Red Springs students of all ages and skill levels.' },
  { name: 'Fayetteville', county: 'Cumberland County', desc: 'Our home base — serving families across Fayetteville with mobile music, vocal coaching, and creative development sessions.' },
  { name: 'Hope Mills', county: 'Cumberland County', desc: 'Mobile music lessons available throughout Hope Mills — piano, vocals, drums, and creative coaching at your home.' },
  { name: 'Spring Lake', county: 'Cumberland County', desc: 'Serving Spring Lake families and the surrounding military community with flexible, fully mobile creative programs.' },
  { name: 'Eastover', county: 'Cumberland County', desc: 'Creative Institute brings quality music instruction and creative development to Eastover families — we come to you.' },
  { name: 'Stedman', county: 'Cumberland County', desc: 'Mobile music and creative development sessions available in Stedman — no commute required for your family.' },
  { name: 'Wade', county: 'Cumberland County', desc: 'Bringing personalized music instruction and creative coaching to Wade students of all ages and backgrounds.' },
  { name: 'Falcon', county: 'Cumberland County', desc: 'Serving Falcon with mobile music lessons and creative development programs tailored to each student\'s goals.' },
  { name: 'Dunn', county: 'Harnett County', desc: 'Mobile music lessons and creative coaching available throughout Dunn and the surrounding Harnett County area.' },
  { name: 'Benson', county: 'Johnston County', desc: 'Creative Institute serves Benson families with personalized, mobile-based music and creative development sessions.' },
  { name: 'Coats', county: 'Harnett County', desc: 'Bringing professional music instruction and creative coaching to Coats — flexible scheduling, we come to you.' },
  { name: 'Lillington', county: 'Harnett County', desc: 'Serving Lillington and surrounding Harnett County communities with fully mobile creative development programs.' },
  { name: 'Sanford', county: 'Lee County', desc: 'Mobile music lessons and creative coaching available throughout Sanford and the greater Lee County area.' },
  { name: 'Fuquay-Varina', county: 'Wake County', desc: 'Bringing top-tier music instruction and creative development to Fuquay-Varina families in the Triangle region.' },
  { name: 'Garner', county: 'Wake County', desc: 'Serving Garner families with mobile music lessons — piano, vocals, drums, and creative coaching at your home.' },
  { name: 'Raleigh', county: 'Wake County', desc: 'Bringing top-tier creative instruction to Raleigh families — we come directly to your home or preferred location.' },
  { name: 'Cary', county: 'Wake County', desc: 'Serving Cary and surrounding areas with flexible scheduling and fully mobile creative development programs.' },
  { name: 'Apex', county: 'Wake County', desc: 'Mobile music and creative development sessions available throughout Apex and the greater Triangle area.' },
  { name: 'Morrisville', county: 'Wake County', desc: 'Creative Institute serves Morrisville families with personalized mobile music lessons and creative coaching programs.' },
  { name: 'Durham', county: 'Durham County', desc: 'Mobile music lessons and creative coaching available throughout Durham and surrounding neighborhoods.' },
  { name: 'Chapel Hill', county: 'Orange County', desc: 'Bringing professional music instruction and creative development to Chapel Hill families across Orange County.' },
];

const programs = [
  { icon: 'ri-music-2-line', title: 'Piano Lessons', desc: 'One-on-one piano instruction for beginners through advanced students, delivered at your home.' },
  { icon: 'ri-mic-line', title: 'Vocal Coaching', desc: 'Professional vocal training, breath control, and performance coaching for all voice types.' },
  { icon: 'ri-rhythm-line', title: 'Drum Lessons', desc: 'Rhythm, technique, and full drum kit instruction — we bring the equipment to you.' },
  { icon: 'ri-palette-line', title: 'Creative Coaching', desc: 'Confidence, expression, and creative development for students of all ages and backgrounds.' },
  { icon: 'ri-record-circle-line', title: 'Music Production', desc: 'Intro to beat-making, recording, and audio production for aspiring producers.' },
  { icon: 'ri-heart-line', title: 'Neurodivergent Programs', desc: 'Specialized creative development designed for students with unique learning needs.' },
];

const citiesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Cities We Serve | Creative Institute LLC',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/cities-we-serve`,
  description: 'Creative Institute LLC serves 22+ cities across North Carolina with mobile music lessons and creative development programs. Fayetteville, Raleigh, Durham, Lumberton, and more.',
  mainEntity: {
    '@type': 'EducationalOrganization',
    name: 'Creative Institute LLC',
    areaServed: [
      { '@type': 'City', name: 'Fayetteville', containedInPlace: { '@type': 'State', name: 'North Carolina' } },
      { '@type': 'City', name: 'Raleigh', containedInPlace: { '@type': 'State', name: 'North Carolina' } },
      { '@type': 'City', name: 'Durham', containedInPlace: { '@type': 'State', name: 'North Carolina' } },
      { '@type': 'City', name: 'Lumberton', containedInPlace: { '@type': 'State', name: 'North Carolina' } },
      { '@type': 'City', name: 'Hope Mills', containedInPlace: { '@type': 'State', name: 'North Carolina' } },
      { '@type': 'City', name: 'Chapel Hill', containedInPlace: { '@type': 'State', name: 'North Carolina' } },
    ],
  },
};

function PageHero() {
  return (
    <section className="relative min-h-[75vh] flex items-end overflow-hidden">
      {/* Drone video background */}
      <div className="absolute inset-0 z-0 bg-black">
        {/* HTML5 video — aerial landscape, autoplays muted and looped */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute w-full h-full object-cover object-center"
          poster="https://public.readdy.ai/ai/img_res/584cdb7c5f6adfb79cf92c5ed5a8fd5c.jpg"
        >
          <source src="https://videos.pexels.com/video-files/15184471/15184471-hd_1920_1080_30fps.mp4" type="video/mp4" />
          <source src="https://videos.pexels.com/video-files/12537781/12537781-hd_1920_1080_30fps.mp4" type="video/mp4" />
        </video>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/65 via-black/40 to-black/65" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/70 to-transparent" />
      </div>

      {/* Hero content */}
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-28 pt-44">
        <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          Serving North Carolina
        </span>
        <h1 className="font-heading text-white text-5xl lg:text-7xl font-black mb-5 leading-tight max-w-4xl">
          Mobile Music Lessons<br />
          <span className="text-brand-yellow">Across North Carolina</span>
        </h1>
        <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed mb-8">
          Creative Institute brings professional music education, vocal coaching, and creative development directly to your home — serving families from Lumberton to Chapel Hill across Robeson, Cumberland, Harnett, Johnston, Lee, Wake, Durham, and Orange counties.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => { const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null; if (btn) btn.click(); }}
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-base cursor-pointer hover:bg-brand-yellow-light transition-all"
          >
            <i className="ri-phone-line text-lg" /> Schedule a Free Call
          </button>
          <Link
            to="/enroll"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/50 text-white font-body font-semibold text-base cursor-pointer hover:border-brand-yellow hover:text-brand-yellow transition-all"
          >
            <i className="ri-arrow-right-line" /> Enroll Now
          </Link>
        </div>
      </div>
    </section>
  );
}

function CitiesGrid() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section className="py-28 bg-white">
      <div className="max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-4`}>
            Our Service Area
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4`}>
            Cities We <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6]">Currently Serve</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed`}>
            We travel to you — no studio required. If your city isn&apos;t listed, give us a call. We&apos;re always expanding.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cities.map((city, i) => (
            <div
              key={city.name}
              className={`scroll-hidden ${gridVis ? 'scroll-visible' : ''} group p-6 rounded-2xl border border-gray-100 hover:border-brand-yellow/40 bg-white hover:bg-gradient-to-br hover:from-[#FFFBEB] hover:to-white transition-all duration-300 card-lift cursor-default`}
              style={{ transitionDelay: `${(i % 6) * 60}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300">
                  <i className="ri-map-pin-2-line text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-heading text-brand-blue-dark text-base font-black">{city.name}</h3>
                    <span className="inline-block px-2 py-0.5 rounded-full bg-[#EEF2FF] text-brand-purple font-body text-[10px] font-semibold">{city.county}</span>
                  </div>
                  <p className="font-body text-gray-500 text-xs leading-relaxed">{city.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Not listed CTA */}
        <div className="mt-12 text-center p-8 rounded-2xl bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF] border border-brand-purple/15">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-purple/15 text-brand-purple mx-auto mb-4">
            <i className="ri-question-line text-xl" />
          </div>
          <h3 className="font-heading text-brand-blue-dark text-xl font-black mb-2">Don&apos;t See Your City?</h3>
          <p className="font-body text-gray-500 text-sm mb-5 max-w-md mx-auto">We&apos;re constantly expanding across North Carolina. Give us a call — we may already serve your area or can make arrangements.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="tel:9102424567" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity">
              <i className="ri-phone-line" /> Call 910-242-4567
            </a>
            <Link to="/contact" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-brand-purple text-brand-purple font-body font-semibold text-sm cursor-pointer hover:bg-brand-purple hover:text-white transition-colors">
              <i className="ri-mail-line" /> Send a Message
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProgramsSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section className="py-24 bg-gradient-to-br from-[#F0F4FF] via-white to-[#F5F3FF]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-blue/8 text-brand-blue-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-blue/15 mb-4">What We Offer</span>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4">
            Programs Available <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6]">In Every City</span>
          </h2>
          <p className="font-body text-gray-500 text-base max-w-xl mx-auto">Every program is delivered mobile — we bring everything needed directly to you.</p>
        </div>
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((p, i) => (
            <div
              key={p.title}
              className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} p-7 rounded-2xl bg-white border border-gray-100 hover:border-brand-yellow/30 card-lift group`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-yellow/15 text-brand-yellow-dark mb-5 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300">
                <i className={`${p.icon} text-xl`} />
              </div>
              <h3 className="font-heading text-brand-blue-dark text-lg font-black mb-2">{p.title}</h3>
              <p className="font-body text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyMobileSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <img src="https://readdy.ai/api/search-image?query=abstract%20musical%20notes%20and%20sound%20waves%20on%20deep%20blue%20background%2C%20elegant%20artistic%20pattern%2C%20no%20text%2C%20wide%20cinematic%20composition%2C%20gold%20and%20white%20accents%20on%20navy%20blue&width=1920&height=700&seq=why-mobile-bg-001&orientation=landscape" alt="" className="w-full h-full object-cover object-top" />
      </div>
      <div className="relative z-10 max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-14`}>
          <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/40 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-5">Why Mobile?</span>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
            Why Families Across NC<br /><span className="text-brand-yellow">Choose Mobile Lessons</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: 'ri-home-heart-line', title: 'Learn at Home', desc: 'Students are more relaxed and focused in their own environment — leading to faster progress.' },
            { icon: 'ri-time-line', title: 'No Commute', desc: 'Save time and gas. No driving across town — we come to your door at your scheduled time.' },
            { icon: 'ri-calendar-check-line', title: 'Flexible Scheduling', desc: 'Morning, afternoon, or evening — we work around your family\'s schedule, not the other way around.' },
            { icon: 'ri-shield-check-line', title: 'Safe & Trusted', desc: 'Background-checked instructors who are vetted, professional, and passionate about student growth.' },
          ].map((item, i) => (
            <div
              key={item.title}
              className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} p-7 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm text-center`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-brand-yellow/20 text-brand-yellow mx-auto mb-5">
                <i className={`${item.icon} text-2xl`} />
              </div>
              <h3 className="font-heading text-white text-base font-black mb-2">{item.title}</h3>
              <p className="font-body text-white/65 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CitiesCTA() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const handleCall = () => { const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null; if (btn) btn.click(); };
  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} max-w-3xl mx-auto text-center`}>
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow mx-auto mb-6">
            <i className="ri-car-line text-brand-blue-dark text-3xl" />
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4 leading-tight">
            Ready to Get Started<br />in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D97706] to-[#7C3AED]">Your City?</span>
          </h2>
          <p className="font-body text-gray-500 text-lg leading-relaxed mb-8 max-w-xl mx-auto">
            Call us for a free consultation. We&apos;ll confirm availability in your area, talk through your goals, and get you set up with the right program.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={handleCall} className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-base cursor-pointer hover:bg-brand-yellow-light transition-all">
              <i className="ri-phone-line text-lg" /> Schedule a Free Call
            </button>
            <Link to="/enroll" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-brand-purple text-brand-purple font-body font-semibold text-base cursor-pointer hover:bg-brand-purple hover:text-white transition-colors">
              <i className="ri-arrow-right-line" /> Enroll Now
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CitiesWeServePage() {
  return (
    <main>
      <SEOHead
        title="Cities We Serve | Creative Institute LLC — Mobile Music Lessons NC"
        description="Creative Institute LLC serves Fayetteville, Raleigh, Durham, Lumberton, Hope Mills, Chapel Hill, and 20+ cities across North Carolina with mobile music lessons and creative development."
        keywords="mobile music lessons NC cities, Creative Institute service area, music lessons Fayetteville NC, music lessons Raleigh NC, music lessons Durham NC, North Carolina arts education"
        canonical="/cities-we-serve"
        jsonLd={citiesJsonLd}
      />
      <PageHero />
      <CitiesGrid />
      <ProgramsSection />
      <WhyMobileSection />
      <CitiesCTA />
    </main>
  );
}
